import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";
import type { ClipboardEvent, DetailedHTMLProps, InputHTMLAttributes } from "react";
import { useCallback, useMemo, useRef, useState } from "react";
import { forwardRef } from "react";
import { CloseIcon } from "../../icons/CloseIcon";
import { HideIcon } from "../../icons/HideIcon";
import { ShowIcon } from "../../icons/ShowIcon";
import { useDesignSystemI18n } from "../../providers/DesignSystemI18nContext";
import { classNames } from "../../utils/classNames";
import { mergeRefs } from "../../utils/merge-refs";
import { IconButton } from "../Button";

export const inputStyles = cva(
	[
		"block w-full rounded-md border border-input text-neutral placeholder:text-placeholder focus:border-primary focus:ring-primary disabled:bg-disabled disabled:text-neutral-faded disabled:hover:cursor-not-allowed aria-disabled:bg-disabled aria-disabled:text-neutral-faded aria-disabled:hover:cursor-not-allowed aria-invalid:border-input-error aria-invalid:ring-critical sm:leading-6",
	],
	{
		variants: {
			icon: {
				left: [""],
				right: [""],
				leftAndRight: [""],
			},
			size: {
				xs: ["text-xs", "py-0.5", "h-7", "px-1"],
				sm: ["text-sm", "py-0.5", "h-8", "px-2"],
				md: ["text-base", "md:text-sm", "py-2", "h-9", "px-3"],
			},
			addOn: {
				left: ["rounded-l-none"],
				right: ["rounded-r-none"],
				leftAndRight: ["rounded-none"],
			},
		},
		compoundVariants: [
			{
				icon: "left",
				size: "sm",
				className: "pl-8",
			},
			{
				icon: "left",
				size: "md",
				className: "pl-10",
			},
			{
				icon: "right",
				size: "sm",
				className: "pr-8",
			},
			{
				icon: "right",
				size: "md",
				className: "pr-10",
			},
			{
				icon: "leftAndRight",
				size: "sm",
				className: "px-8",
			},
			{
				icon: "leftAndRight",
				size: "md",
				className: "px-10",
			},
		],
		defaultVariants: {
			size: "md",
		},
	},
);

export type BaseInputProps = Omit<
	DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
	"size" | "placeholder"
> & {
	size?: VariantProps<typeof inputStyles>["size"];
	leftIcon?: React.ReactNode;
	rightIcon?: React.ReactNode;
	placeholder?: string | undefined | null;
	leftAddOn?: React.ReactNode | string | null;
	rightAddOn?: React.ReactNode | string | null;
	onClear?: () => void;
	/** Required when type="password" - the translated field name for show/hide aria-labels */
	toggleAriaLabel?: string;
};

const BaseInputInner = (props: BaseInputProps, ref: React.ForwardedRef<HTMLInputElement>) => {
	const {
		type = "text",
		leftIcon,
		rightIcon,
		size,
		placeholder,
		leftAddOn,
		rightAddOn,
		onClear,
		disabled,
		defaultValue,
		onPaste,
		toggleAriaLabel,
		...rest
	} = props;
	const [inputType, setInputType] = useState(type);
	const internalRef = useRef<HTMLInputElement>(null);
	const i18n = useDesignSystemI18n();

	const setRefs = useMemo(() => mergeRefs(internalRef, ref), [ref]);

	const handleToggleVisibility = useCallback((newType: "text" | "password") => {
		setInputType(newType);
		setTimeout(() => internalRef.current?.focus(), 0);
	}, []);

	const handlePaste = useCallback(
		(e: ClipboardEvent<HTMLInputElement>) => {
			const pastedText = e.clipboardData?.getData("text") ?? "";
			const trimmedText = pastedText.trim();

			if (pastedText !== trimmedText) {
				e.preventDefault();
				const input = e.currentTarget;
				const start = input.selectionStart ?? 0;
				const end = input.selectionEnd ?? 0;

				input.setRangeText(trimmedText, start, end, "end");

				const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
					window.HTMLInputElement.prototype,
					"value",
				)?.set;
				nativeInputValueSetter?.call(input, input.value);
				input.dispatchEvent(new Event("input", { bubbles: true }));
			}

			onPaste?.(e);
		},
		[onPaste],
	);

	const iconStyle = useMemo(
		() => (leftIcon && rightIcon ? "leftAndRight" : leftIcon ? "left" : rightIcon ? "right" : undefined),
		[leftIcon, rightIcon],
	);

	const addOnStyle = useMemo(
		() => (leftAddOn && rightAddOn ? "leftAndRight" : leftAddOn ? "left" : rightAddOn ? "right" : undefined),
		[leftAddOn, rightAddOn],
	);

	return (
		<div className="relative flex w-full grow">
			{leftAddOn && (
				<span
					className={classNames(
						"inline-flex items-center whitespace-nowrap rounded-l-md border border-input border-r-0 px-3 text-base text-neutral-faded md:text-sm",
						inputType === "range" ? "border-0" : "bg-white",
					)}
				>
					{leftAddOn}
				</span>
			)}
			{leftIcon && (
				<div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">{leftIcon}</div>
			)}
			<input
				type={inputType}
				ref={setRefs}
				id={props.name}
				placeholder={placeholder ?? undefined}
				disabled={disabled}
				defaultValue={defaultValue}
				{...rest}
				onPaste={handlePaste}
				className={classNames(inputStyles({ size, icon: iconStyle, addOn: addOnStyle }), props.className)}
			/>
			{!disabled && onClear && (
				<div className="absolute inset-y-0 right-0 flex items-center pr-3">
					<IconButton size={"xs"} icon={CloseIcon} intent="tertiary" aria-label="Clear" onClick={onClear} />
				</div>
			)}
			{type === "password" && !disabled && (
				<>
					{inputType === "password" && (
						<div className="absolute inset-y-0 right-0 flex items-center pr-3">
							<IconButton
								size={"xs"}
								icon={ShowIcon}
								intent="tertiary"
								aria-label={i18n.show(toggleAriaLabel ?? "")}
								onClick={() => handleToggleVisibility("text")}
							/>
						</div>
					)}
					{inputType === "text" && (
						<div className="absolute inset-y-0 right-0 flex items-center pr-3">
							<IconButton
								size={"xs"}
								icon={HideIcon}
								intent="tertiary"
								aria-label={i18n.hide(toggleAriaLabel ?? "")}
								onClick={() => handleToggleVisibility("password")}
							/>
						</div>
					)}
				</>
			)}
			{rightIcon && (
				<div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">{rightIcon}</div>
			)}
			{rightAddOn && (
				<span
					className={classNames(
						"inline-flex items-center whitespace-nowrap rounded-r-md border border-input border-l-0 px-3 text-base text-neutral-faded md:text-sm",
						inputType === "range" ? "border-0" : "bg-white",
					)}
				>
					{rightAddOn}
				</span>
			)}
		</div>
	);
};

BaseInputInner.displayName = "BaseInput";

export const BaseInput = forwardRef(BaseInputInner);
